import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Hammer, House, ArrowRight, MapPin, Phone, Mail,
  Instagram, Facebook, Youtube, Linkedin, Star, Menu, X
} from "lucide-react";

/* -- Nav -- */
function MCNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-sm border-b border-brand/10" : "bg-transparent"}`}>
      <div className="mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Logo */}
        <Link to="/home" className="group flex items-center gap-2" aria-label="Malicke Case home">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand text-coral shadow-soft transition-transform group-hover:scale-105">
            <Hammer className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-brand">
            Malicke <span className="text-coral">Case</span>
          </span>
        </Link>

        {/* Pill nav  -  desktop */}
        <nav className="hidden md:flex items-center gap-1 rounded-full border border-brand/10 bg-white/60 p-1 backdrop-blur">
          <Link to="/home" className="relative rounded-full px-5 py-1.5 text-sm font-medium transition-colors bg-brand text-white shadow-soft">
            Home
          </Link>
          <Link to="/services" className="relative rounded-full px-5 py-1.5 text-sm font-medium transition-colors text-ink-soft hover:text-brand">
            Services
          </Link>
          <Link to="/listings" className="relative rounded-full px-5 py-1.5 text-sm font-medium transition-colors text-ink-soft hover:text-brand">
            Listings
          </Link>
        </nav>

        {/* Right  -  desktop */}
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+18768279793" className="text-sm font-medium text-ink-soft hover:text-brand transition-colors">
            +1 (876) 827-9793
          </a>
          <Link
            to="/services#booking"
            className="rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white shadow-coral-glow transition-transform hover:scale-105"
          >
            Get estimate
          </Link>
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden grid h-10 w-10 place-items-center rounded-lg text-brand hover:bg-brand/5"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur border-t border-brand/10 px-6 py-4 space-y-3">
          <Link to="/home" className="block text-sm font-semibold text-brand" onClick={() => setOpen(false)}>Home</Link>
          <Link to="/services" className="block text-sm font-medium text-ink-soft" onClick={() => setOpen(false)}>Services</Link>
          <Link to="/listings" className="block text-sm font-medium text-ink-soft" onClick={() => setOpen(false)}>Listings</Link>
          <Link to="/services#booking" className="inline-block rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white mt-2" onClick={() => setOpen(false)}>
            Get estimate
          </Link>
        </div>
      )}
    </header>
  );
}

/* -- Page -- */
export default function LandingPage() {
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  const [footerEmail, setFooterEmail] = useState("");
  const [footerStatus, setFooterStatus] = useState("idle"); // idle | sending | done | error

  async function handleFooterSubscribe(e) {
    e.preventDefault();
    if (!footerEmail) return;
    setFooterStatus("sending");
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: footerEmail }),
      });
      const data = await res.json();
      setFooterStatus(data.error ? "error" : "done");
    } catch {
      setFooterStatus("error");
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans">
      <MCNav />

      <main className="flex-1">

        {/* -- Hero -- */}
        <section className="relative overflow-hidden bg-brand text-white">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="https://sfile.chatglm.cn/images-ppt/093fc02431e6.jpg"
              alt="Modern Caribbean home at dusk"
              className="h-full w-full object-cover animate-ken-burns"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/80 to-brand/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-transparent to-brand-ink/40" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 md:py-36">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-3 py-1 text-xs font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-coral animate-pulse" />
                St. Ann, Jamaica  /  Based in Browns Town
              </div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mt-6 leading-[1.05]">
                Build it.<br />
                Renovate it.<br />
                <span className="text-coral">List it.</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
                We are the perfect crew for the whole job. We do roof repairs, paint, plumb, and
                renovate properties in and around our area of St. Ann. Best part? When the work is
                done, if you're interested, we list the finished homes for rent, lease, and sale for free.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/services"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-semibold shadow-coral-glow hover:scale-[1.03] transition-transform"
                >
                  <Hammer className="h-4 w-4" aria-hidden="true" />
                  Explore our services
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
                <Link
                  to="/listings"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 backdrop-blur px-7 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  <House className="h-4 w-4" aria-hidden="true" />
                  Browse listings
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
              {[
                { value: "Free", label: "Written estimations" },
                { value: "Free", label: "List your property" },
                { value: "1 crew", label: "Repairs to listings" },
                { value: "72 hrs", label: "Estimation turnaround" },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-coral/60 pl-4">
                  <p className="font-display text-3xl md:text-4xl font-bold text-white">{s.value}</p>
                  <p className="text-xs md:text-sm text-white/60 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10" />
        </section>

        {/* -- Two halves, one team -- */}
        <section className="bg-sand">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              {/* Left copy */}
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-coral">Two halves, one team</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-ink mt-3 leading-tight">
                  A construction crew that became a property company.
                </h2>
                <p className="mt-5 text-ink-soft text-lg leading-relaxed">
                  We started Malicke Case as a repair crew working out of St. Ann, fixing roofs and
                  repainting homes in and around the parish. After a while, our clients started asking
                  us to list the properties we'd just finished, so they didn't have to find another team
                  to handle rentals, leases, and sales.
                </p>
                <p className="mt-4 text-ink-soft text-lg leading-relaxed">
                  Today we do both. The same crew that treats your roof can list it for rent when it's done.
                  One phone number, one team accountable for the whole job.
                </p>
              </div>

              {/* Right cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Services card */}
                <Link
                  to="/services"
                  className="group text-left rounded-3xl bg-white border border-brand/10 p-6 shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-brand text-coral">
                    <Hammer className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-ink mt-5">Our services</h3>
                  <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                    Roofing, painting, plumbing, and full renovations. Free written quotes within 72 hours.
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-coral">
                    View services
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>

                {/* Listings card */}
                <Link
                  to="/listings"
                  className="group text-left rounded-3xl bg-brand text-white p-6 shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-coral">
                    <House className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-xl font-bold mt-5">Property listings</h3>
                  <p className="text-sm text-white/70 mt-2 leading-relaxed">
                    Renovated homes for rent, lease, and sale. Each one comes with an interactive map
                    and driving directions.
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-coral">
                    Browse listings
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>

                {/* Testimonial  -  full width */}
                <div className="sm:col-span-2 rounded-3xl bg-coral text-white p-6 shadow-soft">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-white" aria-hidden="true" />
                    ))}
                  </div>
                  <p className="text-lg font-medium leading-snug">
                    "They treated my roof in Runaway Bay, repainted it inside and out, and now it's listed
                    on their site for free. One team for everything. That's rare in Jamaica."
                  </p>
                  <p className="mt-3 text-sm text-white/80">Marcus Walters, homeowner and landlord</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* -- Where we work -- */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left */}
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-coral">Where we work</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-ink mt-3 leading-tight">
                  Based in Browns Town, work wherever we're needed.
                </h2>
                <p className="mt-5 text-ink-soft text-lg leading-relaxed">
                  We're based in Browns Town, St. Ann, and that's where most of our work happens.
                  We also travel for jobs across the parish and beyond. Our listings include properties
                  in Kingston and St. Andrew, where a lot of university students look for places to rent.
                </p>
                <ul className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    "Browns Town (base)", "St. Ann's Bay", "Runaway Bay", "Discovery Bay",
                    "Ocho Rios", "Priory", "Kingston", "Liguanea",
                  ].map((loc) => (
                    <li key={loc} className="flex items-center gap-2 text-sm text-ink">
                      <MapPin className="h-4 w-4 text-coral flex-shrink-0" aria-hidden="true" />
                      {loc}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/listings"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:bg-brand-ink transition-colors"
                >
                  See listings on the map
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>

              {/* Right  -  map */}
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-lifted border border-brand/10">
                <iframe
                  title="Malicke Case service area: Browns Town, St. Ann"
                  src="https://maps.google.com/maps?q=Browns%20Town%20Jamaica&z=10&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <div className="absolute top-4 left-4 rounded-full bg-white/95 backdrop-blur px-4 py-2 text-xs font-semibold text-brand shadow-soft flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-coral" aria-hidden="true" />
                  Based in Browns Town, St. Ann
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* -- Footer -- */}
      <footer className="mt-auto bg-brand-ink text-white">
        {/* CTA strip */}
        <div className="border-b border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-coral text-sm font-semibold tracking-widest uppercase">Let's build</p>
                <h3 className="font-display text-3xl md:text-4xl font-bold mt-2 leading-tight">
                  Got a project in or around St. Ann?
                </h3>
                <p className="text-white/70 mt-3 max-w-md">
                  From a leaking roof to a full renovation, our crew is ready. Free written estimations within 72 hours.
                </p>
              </div>
              <div className="flex md:justify-end gap-3 flex-wrap">
                <Link
                  to="/services#booking"
                  className="rounded-full bg-coral px-6 py-3 text-sm font-semibold shadow-coral-glow hover:scale-105 transition-transform"
                >
                  Free estimate
                </Link>
                <Link
                  to="/listings"
                  className="rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  Browse listings
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer columns */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid gap-10 md:grid-cols-12">
            {/* Brand + newsletter */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10 text-coral">
                  <Hammer className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="font-display text-lg font-bold">
                  Malicke <span className="text-coral">Case</span>
                </span>
              </div>
              <p className="mt-4 text-sm text-white/70 max-w-sm leading-relaxed">
                A St. Ann-based repair, renovation, and property listings team. We fix it, we paint it,
                and we list it, sometimes for free.
              </p>
              {footerStatus === "done" ? (
                <p className="mt-6 text-sm text-white/70">You're on the list. Talk soon.</p>
              ) : (
                <form className="mt-6 flex max-w-sm" onSubmit={handleFooterSubscribe}>
                  <input
                    type="email"
                    required
                    value={footerEmail}
                    onChange={e => setFooterEmail(e.target.value)}
                    placeholder="Your email for project updates"
                    className="flex-1 rounded-l-full bg-white/10 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-coral"
                  />
                  <button
                    type="submit"
                    disabled={footerStatus === "sending"}
                    className="rounded-r-full bg-coral px-4 py-2.5 text-sm font-semibold text-white hover:bg-coral-soft transition-colors disabled:opacity-60"
                    aria-label="Subscribe"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}
              {footerStatus === "error" && (
                <p className="mt-2 text-xs text-red-400">Something went wrong. Try again.</p>
              )}
            </div>

            {/* Pages */}
            <div className="md:col-span-2">
              <p className="text-sm font-semibold text-white/90">Pages</p>
              <ul className="mt-3 space-y-2 text-sm text-white/70">
                <li><Link to="/home" className="hover:text-coral transition-colors">Home</Link></li>
                <li><Link to="/services" className="hover:text-coral transition-colors">Services</Link></li>
                <li><Link to="/listings" className="hover:text-coral transition-colors">Listings</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div className="md:col-span-2">
              <p className="text-sm font-semibold text-white/90">Services</p>
              <ul className="mt-3 space-y-2 text-sm text-white/70">
                {["Roofing", "Painting", "Plumbing", "Full renovation"].map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-3">
              <p className="text-sm font-semibold text-white/90">Contact</p>
              <ul className="mt-3 space-y-3 text-sm text-white/70">
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-coral flex-shrink-0" aria-hidden="true" />
                  <span>St. Ann, Jamaica<br />Based in Browns Town</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-coral flex-shrink-0" aria-hidden="true" />
                  <a href="tel:+18768279793" className="hover:text-coral transition-colors">+1 (876) 827-9793</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-coral flex-shrink-0" aria-hidden="true" />
                  <a href="mailto:hello@malickecase.com" className="hover:text-coral transition-colors">hello@malickecase.com</a>
                </li>
              </ul>
              <div className="mt-4 flex gap-2">
                {[
                  { icon: Instagram, label: "Instagram" },
                  { icon: Facebook, label: "Facebook" },
                  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@malickecase" },
                  { icon: Linkedin, label: "LinkedIn" },
                ].map(({ icon: Icon, label, href }) => (
                  <a
                    key={label}
                    href={href || "#"}
                    aria-label={label}
                    className="grid h-8 w-8 place-items-center rounded-full bg-white/10 hover:bg-coral transition-colors"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/50">
            <p>© {new Date().getFullYear()} Malicke Case Repairs &amp; Listings. All rights reserved.</p>
            <p>St. Ann / Jamaica</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
